/**
 * SimDownloadProgressBanner
 *
 * A fixed bottom banner that shows sim template downloads running in the
 * background. Mirrors the SimUploadProgressBanner pattern.
 *
 * Subscribes to simDownloadQueueStore directly – no Recoil, no prop drilling.
 */

import { FC, useEffect, useState, memo } from 'react'
import { classNames } from 'primereact/utils'
import { LoadingIcon } from './Icons'
import simDownloadQueueStore, {
  PendingSimDownload,
  SimDownloadStatus
} from '../services/simDownloadQueueStore'

const statusLabel: Record<SimDownloadStatus, string> = {
  downloading: 'Downloading from Azure…',
  done: 'Download complete',
  failed: 'Download failed',
  cancelled: 'Download cancelled'
}

const statusColor: Record<SimDownloadStatus, string> = {
  downloading: 'border-gm-blue',
  done: 'border-green-500',
  failed: 'border-red-500',
  cancelled: 'border-gray-400'
}

const statusDot: Record<SimDownloadStatus, string> = {
  downloading: 'bg-gm-blue',
  done: 'bg-green-500',
  failed: 'bg-red-500',
  cancelled: 'bg-gray-400'
}

type DownloadRowProps = {
  download: Omit<PendingSimDownload, 'abortController'>
}

const DownloadRow = memo<DownloadRowProps>(({ download }) => {
  const isActive = download.status === 'downloading'

  return (
    <div
      className={classNames(
        'flex items-center gap-3 px-4 py-2 border-l-4',
        statusColor[download.status]
      )}>
      {/* Status indicator */}
      {isActive ? (
        <LoadingIcon className="w-4 h-4 fill-gm-blue animate-spin flex-shrink-0" />
      ) : (
        <span
          className={classNames('w-3 h-3 rounded-full flex-shrink-0', statusDot[download.status])}
        />
      )}

      {/* Text */}
      <div className="flex flex-col min-w-0 flex-1">
        <span className="text-xs font-medium text-gm-black dark:text-white truncate">
          {download.fileName}
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400 truncate">
          {download.methodologyName} — {statusLabel[download.status]}
          {download.status === 'downloading' && download.progress !== undefined
            ? ` ${download.progress}%`
            : ''}
          {download.status === 'failed' && download.error && `: ${download.error}`}
        </span>
        {/* Progress bar – only shown while actively downloading */}
        {download.status === 'downloading' && (
          <div className="mt-1 w-full bg-gray-200 dark:bg-neutral-70 rounded-full h-1.5 overflow-hidden">
            <div
              className="bg-gm-blue h-1.5 rounded-full transition-all duration-300"
              style={{ width: `${download.progress ?? 0}%` }}
            />
          </div>
        )}
      </div>

      {/* Cancel button for active downloads */}
      {isActive && (
        <button
          className="ml-auto text-gray-400 hover:text-red-500 dark:hover:text-red-400 text-sm flex-shrink-0"
          onClick={() => simDownloadQueueStore.cancel(download.id)}
          title="Cancel download">
          ✕
        </button>
      )}

      {/* Dismiss button for terminal states */}
      {!isActive && (
        <button
          className="ml-auto text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 text-sm flex-shrink-0"
          onClick={() => simDownloadQueueStore.dismiss(download.id)}
          title="Dismiss">
          ✕
        </button>
      )}
    </div>
  )
})

const SimDownloadProgressBanner: FC = () => {
  const [downloads, setDownloads] = useState(simDownloadQueueStore.getAll())

  useEffect(() => {
    const unsub = simDownloadQueueStore.subscribe(setDownloads)
    return unsub
  }, [])

  if (downloads.length === 0) return null

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-50 flex flex-col divide-y divide-gray-200 dark:divide-neutral-70 bg-white dark:bg-dark-blue shadow-lg border-t border-gray-200 dark:border-neutral-70"
      role="status"
      aria-live="polite">
      {/* Header row */}
      <div className="flex items-center justify-between px-4 py-1 bg-light-blue dark:bg-neutral-80">
        <span className="text-xs font-semibold text-gm-blue dark:text-gm-aqua-light uppercase tracking-wide">
          Sim Template Downloads ({downloads.length})
        </span>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          Do not close your browser until all downloads complete
        </span>
      </div>

      {/* One row per download */}
      {downloads.map(d => (
        <DownloadRow key={d.id} download={d} />
      ))}
    </div>
  )
}

export default SimDownloadProgressBanner
